import React, { useState, useEffect, useCallback, useRef } from "react";
import { Crown } from "lucide-react";
import { Button } from "./ui/button";
import { FormField } from "./ui/form_field";
import { useToast } from "./ui/toast";
import { TwoStepModal } from "./ui/two_step_modal";
import { WarningCard } from "./ui/warning_card";
import { MemberSelect, Member } from "./ui/member_select";


interface TransferOwnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceSlug: string;
  workspaceName: string;
  members: Member[];
  onTransferSuccess?: (targetEmail: string) => void;
}

interface TransferFormData {
  selectedMemberId: string;
}

export default function TransferOwnershipModal({
  isOpen,
  onClose,
  workspaceSlug,
  workspaceName,
  members,
  onTransferSuccess,
}: TransferOwnershipModalProps) {
  const { showToast } = useToast();
  const isMountedRef = useRef(true);
  const [formData, setFormData] = useState<TransferFormData>({
    selectedMemberId: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
      setFormData({ selectedMemberId: "" });
      setErrors({});
      setShowConfirmation(false);
    }
  }, [isSubmitting, onClose]);

  // Clear errors when form data changes
  useEffect(() => {
    if (errors.selectedMemberId && formData.selectedMemberId) {
      setErrors((prev) => ({ ...prev, selectedMemberId: "" }));
    }
  }, [formData.selectedMemberId, errors.selectedMemberId]);

  // Helper functions
  const selectedMember = members.find(
    (member) => member.id.toString() === formData.selectedMemberId,
  );

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.selectedMemberId) {
      newErrors.selectedMemberId = "Please select a member";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Show confirmation step
    setShowConfirmation(true);
  };

  const handleConfirmTransfer = async () => {
    if (!selectedMember) return;

    setIsSubmitting(true);

    try {
      const response = await window.fetch(
        `/workspaces/${workspaceSlug}/transfer_ownership`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
          body: JSON.stringify({
            email: selectedMember.email,
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        showToast(
          "success",
          data.message || "Ownership transferred successfully",
        );
        onTransferSuccess?.(selectedMember.email);
        handleClose();
      } else {
        if (data.errors) {
          showToast("error", data.errors.join(", "));
        } else {
          showToast("error", "Failed to transfer ownership. Please try again.");
        }
      }
    } catch (error) {
      console.error("Transfer ownership error:", error);
      showToast("error", "Network error. Please try again.");
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const handleMemberSelect = (memberId: string) => {
    setFormData({ selectedMemberId: memberId });
  };

  const step1Content = (
    <>
      <WarningCard className="mb-6">
        <p>
          {selectedMember
            ? `${selectedMember.name} will become the new owner and will be responsible for billing.`
            : "The selected member will become the new owner and will be responsible for billing."}
        </p>
      </WarningCard>

      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="New owner" error={errors.selectedMemberId}>
          <MemberSelect
            members={members}
            value={formData.selectedMemberId}
            onValueChange={handleMemberSelect}
            placeholder="Select a member to transfer ownership to"
            disabled={isSubmitting}
          />
        </FormField>
      </form>
    </>
  );

  const step1Actions = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={handleClose}
        disabled={isSubmitting}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSubmit}
        disabled={isSubmitting || !selectedMember}
        className="bg-amber-600 hover:bg-amber-700 text-white"
      >
        {selectedMember
          ? `Make ${selectedMember.name} owner`
          : "Select a member"}
      </Button>
    </>
  );

  const step2Content = selectedMember ? (
    <>
      <p className="text-gray-900 dark:text-gray-100 text-center text-lg mb-4">
        Are you sure you want to make{" "}
        <strong>{selectedMember.name}</strong> ({selectedMember.email})
        the new owner of <strong>{workspaceName}</strong>?
      </p>
      <WarningCard variant="centered">
        This action cannot be undone.
      </WarningCard>
    </>
  ) : null;

  const step2Actions = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setShowConfirmation(false)}
        disabled={isSubmitting}
      >
        Go back
      </Button>
      <Button
        onClick={handleConfirmTransfer}
        disabled={isSubmitting}
        className="bg-amber-600 hover:bg-amber-700 text-white"
      >
        {isSubmitting ? "Transferring..." : "Confirm transfer"}
      </Button>
    </>
  );

  return (
    <TwoStepModal
      isOpen={isOpen}
      onClose={handleClose}
      step={showConfirmation ? 2 : 1}
      isSubmitting={isSubmitting}
      icon={Crown}
      step1Title="Transfer Ownership"
      step2Title="Confirm Transfer"
      step1Content={step1Content}
      step2Content={step2Content}
      step1Actions={step1Actions}
      step2Actions={step2Actions}
    />
  );
}
